import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { leadsAPI } from '../../services/api';
import { trackLeadSubmission } from '../../utils/googleAds';

const LeadCaptureForm = ({ variant = 'full', title = 'Get In Touch', subtitle = "We'd love to hear from you" }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interestedIn: 'buying',
    preferredArea: '',
    message: '',
    priceRange: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consentChecked) {
      setError('Please agree to the Terms of Use and Privacy Policy to continue.');
      return;
    }
    setLoading(true);

    try {
      await leadsAPI.create({
        ...formData,
        source: 'website',
      });
      setSubmitted(true);
      // Track Google Ads conversion
      trackLeadSubmission('contact_form');
    } catch (err) {
      setError('Failed to submit. Please try again.');
      console.error('Lead submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-8 text-center">
          <CheckCircle className="mx-auto mb-4 text-green-600" size={48} />
          <h3 className="text-xl font-bold text-green-800 mb-2">Thank You!</h3>
          <p className="text-green-700">
            I've received your information and will be in touch shortly.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-xl border-0">
      <CardHeader className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-t-lg">
        <CardTitle className="text-center">
          <h3 className="text-2xl font-bold">{title}</h3>
          <p className="text-gray-400 text-sm font-normal mt-1">{subtitle}</p>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">First Name *</label>
              <Input
                required
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                placeholder="John"
                className="h-11"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Last Name *</label>
              <Input
                required
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                placeholder="Smith"
                className="h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Email *</label>
              <Input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
                className="h-11"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Phone *</label>
              <Input
                required
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(226) 555-1234"
                className="h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">I'm Interested In</label>
              <Select 
                value={formData.interestedIn} 
                onValueChange={(v) => setFormData({...formData, interestedIn: v})}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buying">Buying a Home</SelectItem>
                  <SelectItem value="selling">Selling a Home</SelectItem>
                  <SelectItem value="both">Buying & Selling</SelectItem>
                  <SelectItem value="investing">Investment Property</SelectItem>
                  <SelectItem value="newbuild">New Construction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Preferred Area *</label>
              <Input
                required
                value={formData.preferredArea}
                onChange={(e) => setFormData({...formData, preferredArea: e.target.value})}
                placeholder="e.g. Brantford, Hamilton, Ancaster"
                className="h-11"
              />
            </div>
          </div>

          {variant === 'full' && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Price Range *</label>
              <Select 
                value={formData.priceRange} 
                onValueChange={(v) => setFormData({...formData, priceRange: v})}
                required
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select Price Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-500">Under $500,000</SelectItem>
                  <SelectItem value="500-750">$500,000 - $750,000</SelectItem>
                  <SelectItem value="750-1m">$750,000 - $1,000,000</SelectItem>
                  <SelectItem value="1m-1.5m">$1,000,000 - $1,500,000</SelectItem>
                  <SelectItem value="over-1.5m">Over $1,500,000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Message</label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Tell me about what you're looking for..."
              rows={4}
            />
          </div>

          <div className="flex items-start gap-2 pt-2">
            <input
              data-testid="lead-form-consent"
              id="leadform-consent"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
            />
            <label htmlFor="leadform-consent" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">Terms of Use</a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">Privacy Policy</a>,
              and consent to receiving real estate updates and communications from Alexander Conti.
            </label>
          </div>

          <Button 
            type="submit" 
            disabled={loading || !consentChecked}
            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={18} />
                Submit
              </span>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Your information is secure and will never be shared with third parties outside our brokerage.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default LeadCaptureForm;
